import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import AIAgentMessageBubble from './AIAgentMessageBubble';
import { AI_AGENT_TOKENS } from '../AIAgentConstants';
import { AIAgentMessage } from '../AIAgentTypes';

interface Props {
  messages: AIAgentMessage[];
  onRetry?: (questionId: string) => void;
  onStartNewConversation?: () => void;
  autoScroll: boolean;
}

export default function AIAgentMessageList({
  messages,
  onRetry,
  onStartNewConversation,
  autoScroll,
}: Props) {
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const visibleMessages = messages.filter((message) => message.role !== 'welcome');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      shouldAutoScrollRef.current = distanceFromBottom < 80;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (
      (autoScroll || shouldAutoScrollRef.current) &&
      bottomRef.current &&
      typeof bottomRef.current.scrollIntoView === 'function'
    ) {
      bottomRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages, autoScroll]);

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 2,
        py: 3,
        backgroundColor: AI_AGENT_TOKENS.surface,
      }}
      aria-label="Histórico da conversa"
    >
      {visibleMessages.map((message) => (
        <AIAgentMessageBubble
          key={message.id}
          message={message}
          onRetry={onRetry}
          onStartNewConversation={onStartNewConversation}
        />
      ))}
      <div ref={bottomRef} />
    </Box>
  );
}
